import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { environment } from 'environments/environment';

interface JwtPayload {
  name?: string;
  sub?: string;
  role?: string;
  exp?: number;
}

@Injectable({
  providedIn: 'root',
})
export class JWTService {
  private helper = new JwtHelperService();
  private readonly TOKEN_KEY = environment.storage.tokenKey;

  constructor(private router: Router) {}

  setToken(token: string): void {
    sessionStorage.setItem(this.TOKEN_KEY, token);
    localStorage.setItem(this.TOKEN_KEY, token);
  }

  getToken(): string | null {
    return (
      sessionStorage.getItem(this.TOKEN_KEY) ||
      localStorage.getItem(this.TOKEN_KEY)
    );
  }

  decodeToken(): JwtPayload | null {
    const jwtToken = this.getToken();
    if (jwtToken) {
      try {
        const decode = this.helper.decodeToken(jwtToken);
        return decode;
      } catch (error) {
        console.error('Erro ao decodificar token:', error);
        return null;
      }
    }
    return null;
  }

  getUser(): string | null {
    const decode = this.decodeToken();
    return decode?.name ?? null;
  }

  getUserId(): string | null {
    const decode = this.decodeToken();
    return decode?.sub ?? null;
  }

  getRole(): string | null {
    const decode = this.decodeToken();
    return decode?.role ?? null;
  }

  /**
   * Verifica se o usuário autenticado possui a role especificada.
   * @param role Nome da role a ser verificada.
   * @returns {boolean} Retorna true se o usuário possuir a role, false caso contrário.
   */
  isRole(role: string): boolean {
    const userRole = this.getRole();
    return userRole === role;
  }

  getExpiryTime(): number | null {
    const decode = this.decodeToken();
    return decode?.exp ?? null;
  }

  isAuthenticated(): boolean {
    const jwtToken = this.getToken();
    if (jwtToken && !this.helper.isTokenExpired(jwtToken)) {
      return true;
    }
    return false;
  }

  clearTokens(): void {
    sessionStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.TOKEN_KEY);
  }
}
